import { RELATED_SERVICE_TYPE_CYCLE, RELATED_SERVICE_TYPE_ONETIME } from '../constants.js';

export const CATALOG_TYPE_PRIMA_KIDS = 'PrimaKids';

/**
 * @property {number} id
 * @property {number} uuid
 * @property {string} name
 * @property {string} slug
 * @property {string} description
 * @property {string} category
 * @property {string} mHubCategoryId;
 * @property {number} order
 * @property {Price} price
 * @property {Price} originalPrice
 * @property {Price} unitPrice
 * @property {?Period} period
 * @property {boolean} isPriceHidden
 * @property {?number} customerOrderId
 * @property {?string} activationDate
 * @property {?string} changeDate
 * @property {Attribute[]} attributes
 * @property {boolean} isVisible - set false to hide the service in most contexts (overview page, eligibility list etc.)
 * @property {boolean} isVisibleInSummary - set false to hide on summary page only (multi-SN page, short list of active services)
 * @property {boolean} isIdRcVerificationRequired
 * @property {boolean} hasUnprocessedRequest
 * @property {boolean} isActive
 * @property {boolean} canBeDeactivated
 * @property {boolean} isEligible
 * @property {boolean} isActivating
 * @property {boolean} isDeactivating
 * @property {boolean} isSubsidy
 * @property {boolean} isRecommended
 * @property {boolean} isInitiallyActive
 * @property {boolean} isInitiallyChanging
 * @property {boolean} isAdded
 * @property {boolean} isRemoved
 * @property {boolean} isUpdated
 * @property {boolean} isAddedInLastUpdate
 * @property {boolean} isRemovedInLastUpdate
 * @property {string} catalogType
 * @property {?TryAndBuy} tryAndBuy
 * @property {boolean} hasActivateButton
 * @property {boolean} hasDeactivateButton
 * @property {?string} customDetailLink
 * @property {?number} relatedServiceUuid
 * @property {?string} relatedServiceType
 * @property {?string} renewalDate
 * @property {boolean} canBeModifiedDuringTariffChange
 */
export class Service {
  constructor(props) {
    const MANDATORY = Symbol('mandatory');

    // List of all properties and their default values
    // Mandatory props have symbol for mandatory and nullable props have null
    const propertyMap = {
      id: MANDATORY,
      uuid: MANDATORY,
      customerOrderId: null,
      name: MANDATORY,
      slug: MANDATORY,
      description: '',
      category: MANDATORY,
      mHubCategoryId: MANDATORY,
      order: MANDATORY,
      price: MANDATORY, // Price object is mandatory, but prices might be null
      originalPrice: MANDATORY, // Price object is mandatory, but prices might be null
      unitPrice: MANDATORY, // Price object is mandatory, but prices might be null
      period: null,
      isPriceHidden: false,
      activationDate: null,
      changeDate: null,
      renewalDate: null,
      attributes: [],
      isVisible: true,
      isVisibleInSummary: true,
      isActive: MANDATORY,
      canBeDeactivated: MANDATORY,
      isEligible: MANDATORY,
      isActivating: MANDATORY,
      isDeactivating: MANDATORY,
      isSubsidy: MANDATORY,
      isRecommended: MANDATORY,
      isIdRcVerificationRequired: MANDATORY,
      hasUnprocessedRequest: false,
      isInitiallyActive: false,
      isInitiallyChanging: false,
      isAdded: false,
      isRemoved: false,
      isUpdated: false,
      isAddedInLastUpdate: false,
      isRemovedInLastUpdate: false,
      catalogType: null,
      tryAndBuy: null,
      hasActivateButton: true,
      hasDeactivateButton: true,
      customDetailLink: null,
      relatedServiceUuid: null,
      relatedServiceType: null,
      canBeModifiedDuringTariffChange: false,
    };

    // Assigns property or it's default value and marks possible
    // missing mandatory props so error can be thrown bellow
    const missingMandatory = [];
    Object.keys(propertyMap).forEach(key => {
      if (props[key] === undefined || props[key] === null) {
        if (propertyMap[key] === MANDATORY) {
          missingMandatory.push(key);
          return;
        }
        this[key] = propertyMap[key];
      } else {
        this[key] = props[key];
      }
    });

    if (missingMandatory.length > 0) {
      throw new Error(
        `Constructing Service object (ID: ${
          props.id
        }) with missing mandatory properties: ${missingMandatory.join(', ')}`,
      );
    }

    // Checks if any extra properties were passed to the constructor
    // and were not mapped to the class by the code above
    const unmappedProperties = [];
    Object.keys(props).forEach(key => {
      if (propertyMap[key] === undefined) {
        unmappedProperties.push(key);
      }
    });

    if (unmappedProperties.length > 0) {
      throw new Error(
        `Constructing Service object (ID: ${
          props.id
        }) with unmapped properties: ${unmappedProperties.join(', ')}`,
      );
    }
  }

  /**
   *
   * @param {string} type
   * @returns {?Attribute}
   */
  getAttributeByType(type) {
    const attribute = this.attributes.find(a => a.type === type);
    return attribute || null;
  }

  /**
   * @return {boolean}
   */
  get isChanged() {
    return this.isAdded || this.isRemoved || this.isUpdated;
  }

  /**
   *
   * @returns {boolean}
   */
  get isInProcessing() {
    return this.isActivating || this.isDeactivating;
  }

  /**
   * Returns url to service detail.
   *
   * @param {string} detailLink - link to products and services detail page
   * @returns {string}
   */
  getDetailUrl(detailLink) {
    if (this.customDetailLink !== null) {
      return this.customDetailLink;
    }

    const query = new URLSearchParams();
    query.set('sluzba', `${this.uuid}-${this.slug}`);

    return `${detailLink}?${query}`;
  }

  /**
   * Checks whether related service is version of the current service
   * with or without automatic renewal.
   *
   * @returns {boolean}
   */
  get hasTwinRelatedService() {
    return (
      this.relatedServiceType === RELATED_SERVICE_TYPE_ONETIME ||
      this.relatedServiceType === RELATED_SERVICE_TYPE_CYCLE
    );
  }
}
